import React, { useEffect, useRef, useState } from 'react';
import Styles from './styles.module.css';
import { applicationInfoData, benefitData, employmentData17, employmentData18, employmentData20, infoData, targetData } from 'src/type';
import { IoChevronDown } from "react-icons/io5";
import { MdOutlineApartment, MdOutlinePayments, MdOutlineVolunteerActivism, MdOutlineGroups, MdOutlineAssignment } from "react-icons/md";
import Title from "./../title/index";

// 求人票1年度分（求人情報セクションに出す一式）。卒業年度タブでの切り替え用。
// memorii（学生アプリ）本体の RecruitInfoYearData（9.info.dart）に相当。
export type JobPostingYearBundle = {
  info: infoData;
  target: targetData;
  benefit: benefitData;
  employment20: employmentData20;
  employment18: employmentData18;
  employment17: employmentData17;
  applicationInfo: applicationInfoData;
};

type RecruitPageEmployeeProps = JobPostingYearBundle & {
  // 卒業年度タブ（2027年卒／2028年卒…）。実データのある年度が1件以上ある
  // 会社は、20歳卒/18歳卒/17歳卒の行の上にタブを出す。0件（jobPostings
  // 未対応の会社、または &job 無し）なら従来どおり上の単年度propsのまま
  // 表示する。
  jobPostingYearsAvailable?: string[];
  jobPostingYearData?: Record<string, JobPostingYearBundle>;
};

type Field = { label: string; value: string; error?: string };

// ---- pure helpers ----

function formatYen(value: string): string {
  const n = parseInt(value, 10);
  if (isNaN(n)) return value;
  return n.toLocaleString('ja-JP');
}

function formatDateToJapanese(dateStr?: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[0]}年${parseInt(parts[1])}月${parseInt(parts[2])}日`;
}

function parseOrZero(s: string | undefined | null): number {
  return parseInt(s ?? '0', 10) || 0;
}

function totalSalary(emp: any): number {
  const base = parseOrZero(emp.baseSalary);
  const a1 = parseOrZero(emp.regularAllowance?.price1);
  const a2 = parseOrZero(emp.regularAllowance?.price2);
  const ft = parseOrZero(emp.fixedOvertime?.price);
  const sum = base + a1 + a2 + ft;
  return emp.salaryType === '年俸制' ? sum * 12 : sum;
}

function buildInfoFields(info: infoData): Field[] {
  return [
    { label: '企業名', value: info.companyName ?? '' },
    { label: '企業名（フリガナ）', value: info.companyNameKana ?? '' },
    { label: '住所', value: info.address ?? '' },
    { label: '住所（フリガナ）', value: info.addressKana ?? '' },
    { label: '代表者', value: info.ownerName ?? '' },
    { label: '代表者（フリガナ）', value: info.ownerNameKana ?? '' },
    { label: '設立（年）', value: info.establishedDate ?? '' },
    { label: '従業員数（人）', value: info.numberOfEmployees ?? '' },
    { label: '電話番号', value: info.phoneNumber ?? '' },
    { label: 'メール', value: info.email ?? '' },
    { label: 'ホームページ', value: info.homepageUrl ?? '' },
    { label: 'インスタグラム', value: info.instagram ?? '' },
    { label: '採用担当', value: info.recruiterName ?? '' },
    { label: '採用担当（フリガナ）', value: info.recruiterNameKana ?? '' },
    { label: '事業内容', value: Array.isArray(info.business?.contents) ? info.business.contents.join('、') : '' },
    { label: '兼任', value: (info.business as any)?.canConcurrent === true ? '兼任可能' : '兼任不可' },
    { label: '特例措置対象事業所の有無', value: info.isSpecialExemption ? '特例措置対象事業所である' : '特例措置対象事業所ではない' },
    { label: '36協定の有無', value: (info as any).exist36Agreement === true ? '36協定を提出済み' : '36協定を未提出' },
  ];
}

function buildEmploymentFields(emp: any, info: infoData, target: targetData): Field[] {
  const fields: Field[] = [];

  fields.push({ label: '雇用形態', value: emp.type ?? '' });
  fields.push({ label: '労働制度', value: emp.workSystem ?? '' });

  if (emp.workSystem === '1か月単位変形労働時間制') {
    const v = emp.variableWork ?? {};
    const value = [
      v.hours28Days ? `28日：${v.hours28Days}時間` : '',
      v.hours29Days ? `29日：${v.hours29Days}時間` : '',
      v.hours30Days ? `30日：${v.hours30Days}時間` : '',
      v.hours31Days ? `31日：${v.hours31Days}時間` : '',
    ].filter(Boolean).join('、');

    const has36 = (info as any).exist36Agreement === true;
    const isSpecial = info.isSpecialExemption === true;
    const limits: [string, number, number][] = [
      ['28Days', 176, 160],
      ['29Days', 182.2, 165.7],
      ['30Days', 188.5, 171.4],
      ['31Days', 194.8, 177.1],
    ];
    const warns: string[] = [];
    if (!has36) warns.push('⚠️警告：36協定を締結していないため、従業員に残業をさせることはできません。');
    for (const [key, limitSpecial, limitNormal] of limits) {
      const val = v[`hours${key}`];
      const limit = isSpecial ? limitSpecial : limitNormal;
      if (val && parseFloat(val) > limit) {
        warns.push(`※1か月単位変形労働時間制の${key.replace('Days', '日')}/月の場合、${limit}時間が上限として定められています。`);
      }
    }
    fields.push({ label: '1か月単位変形労働時間制', value, error: warns.join('\n') || undefined });
  }

  if (emp.workSystem === '1年単位変形労働時間制') {
    const v = emp.variableWork ?? {};
    const value = `1年：${v.hoursYearly ?? ''}時間`;
    const has36 = (info as any).exist36Agreement === true;
    const warns: string[] = [];
    if (!has36) warns.push('⚠️警告：36協定を締結していないため、従業員に残業をさせることはできません。');
    if (v.hoursYearly && parseFloat(v.hoursYearly) > 2085) {
      warns.push('※警告：1年間の労働時間が2085時間を超える場合、36協定の締結が必要になります。');
    }
    fields.push({ label: '1年単位変形労働時間制', value, error: warns.join('\n') || undefined });
  }

  const targetArr: string[] = Array.isArray(target?.target) ? target.target : [];
  fields.push({ label: '募集区分', value: targetArr.length > 0 ? targetArr.join('、') : '' });

  fields.push({
    label: '雇用期間',
    value: emp.contractPeriod?.exist === true
      ? `定め有り　期間：${emp.contractPeriod.text}ヶ月`
      : '定め無し',
  });

  fields.push({
    label: '試用期間',
    value: emp.employmentTrial?.exist === true
      ? `定め有り　期間：${emp.employmentTrial.text}ヶ月　条件：${emp.employmentTrial.conditions ?? ''}`
      : '定め無し',
  });

  fields.push({ label: '基本給', value: emp.baseSalary ? `${formatYen(emp.baseSalary)}円` : '' });
  fields.push({ label: '賃金形態', value: emp.salaryType ?? '' });

  const isMonthlySalary = emp.salaryType === '年俸制' || emp.salaryType === '月給';
  const isVariableWork = emp.workSystem === '1か月単位変形労働時間制' || emp.workSystem === '1年単位変形労働時間制';

  if (isMonthlySalary && !isVariableWork) {
    const avgH = parseFloat(emp.avgMonthlyHours ?? '0') || 0;
    fields.push({
      label: '平均労働時間/月',
      value: `${emp.avgMonthlyHours}時間`,
      error: avgH > 206 ? '⚠️警告：1ヶ月間の総労働時間上限は206時間と定められています。' : undefined,
    });
  }

  if (emp.salaryType === '日給') {
    fields.push({ label: '労働時間/日', value: `${emp.dailyHours}時間` });
  }

  if (isMonthlySalary) {
    fields.push({
      label: '定額手当①',
      value: emp.regularAllowance?.allowance1
        ? `${emp.regularAllowance.allowance1}　金額：${formatYen(emp.regularAllowance.price1)}円 /月`
        : '',
    });
    fields.push({
      label: '定額手当②',
      value: emp.regularAllowance?.allowance2
        ? `${emp.regularAllowance.allowance2}　金額：${formatYen(emp.regularAllowance.price2)}円 /月`
        : '',
    });
  }

  if (!isVariableWork) {
    const ftValue = emp.fixedOvertime?.exist === true
      ? `固定残業代を含む　${formatYen(emp.fixedOvertime.price)}円支給 / ${emp.fixedOvertime.time}時間分\n※超過分は全額支給`
      : '支給無し';
    fields.push({ label: '固定残業代', value: ftValue });
  }

  const total = totalSalary(emp);
  fields.push({ label: '総支給額', value: `${total.toLocaleString('ja-JP')}円` });
  fields.push({ label: 'インセンティブ', value: emp.incentive ?? '' });

  return fields;
}

function buildBenefitFields(benefit: benefitData, info: infoData): Field[] {
  const fields: Field[] = [];

  const commute = benefit.commutingAllowance;
  const commuteValue =
    commute?.exist === '実費支給（上限有り）' || commute?.exist === '一定額'
      ? `支給有り　金額：${commute.text}`
      : commute?.exist === '実費支給（上限無し）'
      ? '支給有り　金額：全額支給'
      : commute?.exist ?? '';
  fields.push({ label: '通勤手当', value: commuteValue });

  const has36 = (info as any).exist36Agreement === true;
  const hasOvertime = benefit.overtime?.exist === true;
  fields.push({
    label: '時間外労働の有無',
    value: hasOvertime ? '時間外労働有り' : '時間外労働無し',
    error: (!has36 && hasOvertime) ? '※警告：法定労働時間外の業務には、原則36協定の締結が必要になります。' : undefined,
  });

  const overtimeHours = parseFloat(benefit.overtime?.text ?? '0') || 0;
  fields.push({
    label: '時間外労働',
    value: hasOvertime ? `有り　平均時間：${benefit.overtime?.text}時間 / 月` : '時間外労働無し',
    error: overtimeHours > 45 ? '※警告：時間外労働の月平均（時間）の上限は45時間と定められています。' : undefined,
  });

  const wh = benefit.workingHours ?? {} as any;
  const workingTime = [
    wh.time1Start ? `①${wh.time1Start}~${wh.time1End}` : '',
    wh.time2Start ? `②${wh.time2Start}~${wh.time2End}` : '',
    wh.time3Start ? `③${wh.time3Start}~${wh.time3End}` : '',
  ].filter(Boolean).join('　') + `（休憩：${benefit.dailyBreak ?? ''}分）`;
  fields.push({ label: '勤務時間', value: workingTime });

  const regularHolidays = Array.isArray(benefit.regularHolidays)
    ? [...new Set(benefit.regularHolidays)].join('、')
    : (benefit.regularHolidays as any) ?? '';
  fields.push({ label: '定休日', value: regularHolidays });

  const holidayPolicy = benefit.holidayPolicy?.exist === 'その他'
    ? benefit.holidayPolicy.text
    : benefit.holidayPolicy?.exist ?? '';
  fields.push({ label: '休日制度', value: holidayPolicy });

  const daysOff = Array.isArray(benefit.daysOff)
    ? [...new Set(benefit.daysOff)].join('、')
    : (benefit.daysOff as any) ?? '';
  fields.push({ label: '休暇', value: daysOff });

  const paidLeave = parseFloat(benefit.paidLeave ?? '0') || 0;
  fields.push({
    label: '有給休暇数',
    value: `${benefit.paidLeave}日　年間休日：${benefit.annualHolidays}日`,
    error: paidLeave < 10 ? '※警告：週5日出勤の場合、年次有給休暇数の下限は10日と定められています。' : undefined,
  });

  const insurance = Array.isArray(benefit.insurance)
    ? [...new Set(benefit.insurance)].join('、')
    : (benefit.insurance as any) ?? '';
  fields.push({ label: '加入保険', value: insurance });
  fields.push({ label: 'その他福利厚生', value: benefit.otherText ?? '' });
  fields.push({ label: '受動喫煙対策', value: benefit.tabaco ?? '' });

  return fields;
}

function buildRecruitFields(applicationInfo: applicationInfoData): Field[] {
  return [
    { label: '理容', value: applicationInfo.recruitsBarber ? `${applicationInfo.recruitsBarber} 人` : '0 人' },
    { label: '美容', value: applicationInfo.recruitsBeauty ? `${applicationInfo.recruitsBeauty} 人` : '0 人' },
    { label: 'メイク', value: applicationInfo.recruitsMake ? `${applicationInfo.recruitsMake} 人` : '0 人' },
    { label: 'ネイル', value: applicationInfo.recruitsNeil ? `${applicationInfo.recruitsNeil} 人` : '0 人' },
    { label: 'アイ', value: applicationInfo.recruitsEye ? `${applicationInfo.recruitsEye} 人` : '0 人' },
    { label: 'ブライダル', value: applicationInfo.recruitsBridal ? `${applicationInfo.recruitsBridal} 人` : '0 人' },
    { label: 'エステ', value: applicationInfo.recruitsAesthetic ? `${applicationInfo.recruitsAesthetic} 人` : '0 人' },
  ];
}

function buildApplicationFields(applicationInfo: applicationInfoData): Field[] {
  return [
    { label: '応募書類', value: Array.isArray(applicationInfo.applicationDocuments) ? applicationInfo.applicationDocuments.join('、') : (applicationInfo.applicationDocuments as any) ?? '' },
    { label: '応募方法', value: applicationInfo.submissionMethod ?? '' },
    { label: '店舗見学', value: applicationInfo.storeTour ?? '' },
    { label: '応募期限', value: formatDateToJapanese(applicationInfo.selectionDate) },
    { label: '1次選考', value: applicationInfo.selectionRound1 ?? '' },
    { label: '2次選考', value: applicationInfo.selectionRound2 ?? '' },
    { label: '3次選考', value: applicationInfo.selectionRound3 ?? '' },
    { label: 'その他試験情報', value: applicationInfo.selectionOtherDetails ?? '' },
  ];
}

// ---- component ----

const RecruitPageEmployee: React.FC<RecruitPageEmployeeProps> = ({
  info: defaultInfo,
  target: defaultTarget,
  benefit: defaultBenefit,
  employment20: defaultEmployment20,
  employment18: defaultEmployment18,
  employment17: defaultEmployment17,
  applicationInfo: defaultApplicationInfo,
  jobPostingYearsAvailable,
  jobPostingYearData,
}) => {

  // 卒業年度タブ（2027年卒／2028年卒…）。実データのある年度が1件以上ある
  // 会社だけタブを出し、選択中の年度が無い/データが無い場合は従来どおり
  // 上の単年度propsにフォールバックする（memorii本体RecruitInfoStateの
  // _currentYearData ?? widget.xxxList と同じ構造）。
  const [selectedYear, setSelectedYear] = useState<string | null>(null);

  useEffect(() => {
    if (!jobPostingYearsAvailable || jobPostingYearsAvailable.length === 0) return;
    setSelectedYear((prev) => (prev && jobPostingYearsAvailable.includes(prev) ? prev : jobPostingYearsAvailable[0]));
  }, [jobPostingYearsAvailable]);

  const currentYearBundle = selectedYear ? jobPostingYearData?.[selectedYear] : undefined;
  const info = currentYearBundle?.info ?? defaultInfo;
  const target = currentYearBundle?.target ?? defaultTarget;
  const benefit = currentYearBundle?.benefit ?? defaultBenefit;
  const employment20 = currentYearBundle?.employment20 ?? defaultEmployment20;
  const employment18 = currentYearBundle?.employment18 ?? defaultEmployment18;
  const employment17 = currentYearBundle?.employment17 ?? defaultEmployment17;
  const applicationInfo = currentYearBundle?.applicationInfo ?? defaultApplicationInfo;

  const targetArr: string[] = Array.isArray(target?.target) ? target.target : [];
  const [selectedTarget, setSelectedTarget] = useState<'20' | '18' | '17'>('20');

  const [isOpen1, setIsOpen1] = useState(true);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);
  const [isOpen4, setIsOpen4] = useState(false);
  const [isOpen5, setIsOpen5] = useState(false);

  const contentRef1 = useRef<HTMLDivElement>(null);
  const contentRef2 = useRef<HTMLDivElement>(null);
  const contentRef3 = useRef<HTMLDivElement>(null);
  const contentRef4 = useRef<HTMLDivElement>(null);
  const contentRef5 = useRef<HTMLDivElement>(null);

  const [maxHeight1, setMaxHeight1] = useState('5000px');
  const [maxHeight2, setMaxHeight2] = useState('0px');
  const [maxHeight3, setMaxHeight3] = useState('0px');
  const [maxHeight4, setMaxHeight4] = useState('0px');
  const [maxHeight5, setMaxHeight5] = useState('0px');

  useEffect(() => {
    if (contentRef1.current) setMaxHeight1(isOpen1 ? `${contentRef1.current.scrollHeight}px` : '0px');
  }, [isOpen1, info]);

  useEffect(() => {
    if (contentRef2.current) setMaxHeight2(isOpen2 ? `${contentRef2.current.scrollHeight}px` : '0px');
  }, [isOpen2, selectedTarget, employment20, employment18, employment17, target]);

  useEffect(() => {
    if (contentRef3.current) setMaxHeight3(isOpen3 ? `${contentRef3.current.scrollHeight}px` : '0px');
  }, [isOpen3, benefit, info]);

  useEffect(() => {
    if (contentRef4.current) setMaxHeight4(isOpen4 ? `${contentRef4.current.scrollHeight}px` : '0px');
  }, [isOpen4, applicationInfo]);

  useEffect(() => {
    if (contentRef5.current) setMaxHeight5(isOpen5 ? `${contentRef5.current.scrollHeight}px` : '0px');
  }, [isOpen5, applicationInfo]);

  const empFields =
    selectedTarget === '20' ? buildEmploymentFields(employment20, info, target)
    : selectedTarget === '18' ? buildEmploymentFields(employment18, info, target)
    : buildEmploymentFields(employment17, info, target);

  // 項目(学生アプリの _newField と同じ。項目名は小さい灰色、値は14px、警告は黄色の枠)
  const renderField = (f: Field, i: number) => (
    <div key={i} className={Styles.field}>
      <h4>{f.label}</h4>
      <p>{f.value && f.value.trim() ? f.value : '-'}</p>
      {f.error && <p className={Styles.field_warn}>{f.error}</p>}
    </div>
  );

  const SECTION_ICONS: Record<string, React.ReactNode> = {
    '企業情報': <MdOutlineApartment />,
    '雇用情報': <MdOutlinePayments />,
    '福利厚生': <MdOutlineVolunteerActivism />,
    '募集人数': <MdOutlineGroups />,
    '応募情報': <MdOutlineAssignment />,
  };

  // 白いカードの開閉(アイコンの丸＋見出し＋▼)
  const renderAccordion = (
    title: string,
    isOpen: boolean,
    toggle: () => void,
    contentRef: React.RefObject<HTMLDivElement>,
    maxHeight: string,
    children: React.ReactNode,
  ) => (
    <div className={Styles.card}>
      <button type="button" onClick={toggle} className={Styles.card_title} aria-expanded={isOpen}>
        <span className={Styles.card_icon}>{SECTION_ICONS[title]}</span>
        <span className={Styles.card_name}>{title}</span>
        <span className={Styles.card_chevron} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: isOpen ? '#E44E70' : '#6B6B6B' }}>
          <IoChevronDown />
        </span>
      </button>
      <div ref={contentRef} className={Styles.card_contents} style={{ maxHeight }}>
        <div className={Styles.card_inner}>{children}</div>
      </div>
    </div>
  );

  const pill = (label: string, selected: boolean, onClick: () => void, enabled = true) => (
    <button
      type="button"
      key={label}
      className={selected ? `${Styles.pill} ${Styles.pill_on}` : Styles.pill}
      style={enabled ? undefined : { opacity: 0.4 }}
      onClick={onClick}>
      {label}
    </button>
  );

  return (
    <>
    <div className={Styles.content}>

      <div className={Styles.hr} />
      <Title title="求人票" desc="雇用条件・福利厚生・応募方法などの詳細" />

      {renderAccordion('企業情報', isOpen1, () => setIsOpen1(v => !v), contentRef1, maxHeight1,
        buildInfoFields(info).map(renderField)
      )}

      {renderAccordion('雇用情報', isOpen2, () => setIsOpen2(v => !v), contentRef2, maxHeight2, <>
        {jobPostingYearsAvailable && jobPostingYearsAvailable.length > 0 && (
          <div className={Styles.pills}>
            {jobPostingYearsAvailable.map((year) => pill(`${year}年卒`, selectedYear === year, () => setSelectedYear(year)))}
          </div>
        )}
        <div className={Styles.pills}>
          {pill('専門(20歳卒)', selectedTarget === '20', () => setSelectedTarget('20'))}
          {pill('高専(18歳卒)', selectedTarget === '18', () => { if (targetArr.includes('高専(18歳卒)')) setSelectedTarget('18'); }, targetArr.includes('高専(18歳卒)'))}
          {pill('高専(17歳卒)', selectedTarget === '17', () => { if (targetArr.includes('高専(17歳卒)')) setSelectedTarget('17'); }, targetArr.includes('高専(17歳卒)'))}
        </div>
        {empFields.map(renderField)}
      </>)}

      {renderAccordion('福利厚生', isOpen3, () => setIsOpen3(v => !v), contentRef3, maxHeight3,
        buildBenefitFields(benefit, info).map(renderField)
      )}

      {renderAccordion('募集人数', isOpen4, () => setIsOpen4(v => !v), contentRef4, maxHeight4,
        buildRecruitFields(applicationInfo).map(renderField)
      )}

      {renderAccordion('応募情報', isOpen5, () => setIsOpen5(v => !v), contentRef5, maxHeight5,
        buildApplicationFields(applicationInfo).map(renderField)
      )}

    </div>
    </>
  );
};

export default RecruitPageEmployee;
